import { writeDB, api, storedData } from './store';
import { resetDB } from './state';
import { API } from './api';

/* INTERFACES */
export interface Session {
  language: string;
  userId?: string;
  membershipIds: string[];
}

function parseSession(data: Awaited<ReturnType<API['createSession']>>['data']): Session {
  return {
    ...data,
    membershipIds: data.memberships.map((e) => e.id),
  };
}

/* SESSION CREATE */
export async function createSession(params: {
  language: string;
  email?: string | null;
  password?: string | null;
  rememberLogin?: boolean;
}): Promise<void> {
  const response = await api.createSession(params);
  const session = parseSession(response.data);

  // Clear any old access restriction errors
  writeDB
    .table('notifications')
    .where((notif) => notif.title.includes('401') || notif.title.includes('403'))
    .forEach((notif) => {
      writeDB.table('notifications').delete(notif.id);
    });

  writeDB.transaction((db, t) => {
    t(db.set('session', session));
    t(db.table('memberships').upsert(response.data.memberships));
    t(db.set('selectedMembership', session.membershipIds[0]));
  });
}

/* SESSION READ */
export async function refreshSession(): Promise<void> {
  try {
    const response = await api.showSession();
    const session = parseSession(response.data);

    if (session && !storedData) {
      writeDB.transaction((db, t) => {
        t(db.table('memberships').upsert(response.data.memberships));
        t(db.set('selectedMembership', session.membershipIds[0]));
      });
    }

    writeDB.set('session', session);
    writeDB.table('memberships').upsert(response.data.memberships);

    const selectedMembership = writeDB.get('selectedMembership');
    const memberships = writeDB.table('memberships').where({ id: selectedMembership });

    const membershipExists = selectedMembership
      ? memberships.find((mem) => selectedMembership === mem.id) // checking if the old membership is in the new session memberships
      : memberships[0]; // no membership in memory: picking up the first from the session
    if (membershipExists) {
      writeDB.set('selectedMembership', membershipExists.id);
    }
  } catch (error) {
    writeDB.set('session', undefined);
    writeDB.set('selectedMembership', undefined);
  }
}

/* SESSION UPDATE */
/* SESSION DELETE */
export async function destroySession(): Promise<void> {
  await api.destroySession();
  writeDB.set('session', undefined);
  writeDB.set('selectedMembership', undefined);
  resetDB();
  // Remove settings
  writeDB.set('printLabel', false);
  writeDB.set('printTickets', false);
}

/* HELPERS */
export async function resetPassword({ email }: { email: string }): Promise<void> {
  await api.createPasswordReset({ email });
}

export async function confirmPasswordReset({
  resetToken,
  password,
}: {
  resetToken: string;
  password: string;
}): Promise<{ email: string }> {
  const response = await api.updatePasswordReset({ resetToken, password });
  return new Promise((resolve) => {
    if (response) {
      resolve(response.data);
    }
  });
}
